import { useMemo, useState } from 'react';
import { Col, Container, Row, Tab, Nav, TabContent } from 'react-bootstrap';
import { useLoaderData, useParams } from 'react-router-dom';
import { PaymentReport } from './Reports/PaymentReport';
import { SubscriptionReport } from './Reports/SubscriptionReport';
import './ReportsPage.css';
import { ServerTransferSavedReportResponse, ServerTransferRecurringReportScheduleResponse } from './shared/api/axios-client';
import { RecurringReports } from './Reports/RecurringReports';
import { ArrowRepeat } from 'react-bootstrap-icons';
import { AgingReport } from './Reports/AgingReport';
import { RedeemedValidationReport } from './Reports/RedeemedValidationReport';
import { CustomReport } from './Reports/CustomReport';

export function ReportsPage() {
    const { facilityId } = useParams<{ facilityId: string }>();
    const parsedFacilityId = facilityId ? parseInt(facilityId, 10) : undefined;
    const [initialPaymentReports, initialSubscriptionReports, initialAgingReports, initialRedeemedValidationReports, recurringReports, hasCustomReports, initialCustomSavedReports] = useLoaderData() as [
        ServerTransferSavedReportResponse[],
        ServerTransferSavedReportResponse[],
        ServerTransferSavedReportResponse[],
        ServerTransferSavedReportResponse[],
        ServerTransferRecurringReportScheduleResponse[],
        boolean,
        ServerTransferSavedReportResponse[],
    ];
    const [paymentSavedReports, setPaymentSavedReports] = useState(initialPaymentReports);
    const [subscriptionSavedReports, setSubscriptionSavedReports] = useState(initialSubscriptionReports);
    const [agingSavedReports, setAgingSavedReports] = useState(initialAgingReports);
    const [redeemedValidationsSavedReports, setRedeemedValidationsSavedReports] = useState(initialRedeemedValidationReports);
    const [customSavedReports, setCustomSavedReports] = useState(initialCustomSavedReports);

    const [activeTab, setActiveTab] = useState('payments');
    const allSavedReports = useMemo(() => [
        ...paymentSavedReports.map((r) => ({ id: r.savedReportId!, name: r.name!, type: r.type! })),
        ...subscriptionSavedReports.map((r) => ({ id: r.savedReportId!, name: r.name!, type: r.type! })),
        ...agingSavedReports.map((r) => ({ id: r.savedReportId!, name: r.name!, type: r.type! })),
        ...redeemedValidationsSavedReports.map((r) => ({ id: r.savedReportId!, name: r.name!, type: r.type! })),
        ...customSavedReports.map((r) => ({ id: r.savedReportId!, name: r.name!, type: r.type! })),
    ], [paymentSavedReports, subscriptionSavedReports, agingSavedReports, redeemedValidationsSavedReports, customSavedReports]);

    return (
        <Container fluid className="h-100 d-flex flex-column overflow-hidden">
            <Row className="flex-grow-1">
                <Col className="h-100">
                    <Nav variant="tabs" activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'payments')} className="mb-3 d-flex">
                        <Nav.Item>
                            <Nav.Link eventKey="payments">Revenue</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="subscriptions">Subscriptions</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="aging">Aging</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="redeemed-validations">Redeemed Validations</Nav.Link>
                        </Nav.Item>
                        {hasCustomReports && (
                            <Nav.Item>
                                <Nav.Link eventKey="custom">Custom Reports</Nav.Link>
                            </Nav.Item>
                        )}
                        <Nav.Item className="ms-auto">
                            <Nav.Link eventKey="recurring">
                                <ArrowRepeat className="me-1 icon-shift-up" />
                                Recurring Reports
                            </Nav.Link>
                        </Nav.Item>
                    </Nav>
                    
                    <TabContent>
                        <Tab.Pane eventKey="payments" active={activeTab === 'payments'}>
                       <PaymentReport
                            facilityId={parsedFacilityId}
                            savedReports={paymentSavedReports}
                            setSavedReports={setPaymentSavedReports}
                        />
                        </Tab.Pane>
                        <Tab.Pane eventKey="subscriptions" active={activeTab === 'subscriptions'}>
                        <SubscriptionReport
                            facilityId={parsedFacilityId}
                            savedReports={subscriptionSavedReports}
                            setSavedReports={setSubscriptionSavedReports}
                        />
                        </Tab.Pane>
                        <Tab.Pane eventKey="aging" active={activeTab === 'aging'}> 
                                <AgingReport
                                    facilityId={parsedFacilityId}
                                    savedReports={agingSavedReports}
                                    setSavedReports={setAgingSavedReports}
                                />
                        </Tab.Pane>
                        <Tab.Pane eventKey="redeemed-validations" active={activeTab === 'redeemed-validations'}> 
                                <RedeemedValidationReport
                                    facilityId={parsedFacilityId}
                                    savedReports={redeemedValidationsSavedReports}
                                    setSavedReports={setRedeemedValidationsSavedReports}
                                />
                        </Tab.Pane>
                        {hasCustomReports && (
                            <Tab.Pane eventKey="custom" active={activeTab === 'custom'}>
                                <CustomReport
                                    facilityId={parsedFacilityId}
                                    savedReports={customSavedReports}
                                    setSavedReports={setCustomSavedReports}
                                />
                            </Tab.Pane>
                        )}
                        <Tab.Pane eventKey="recurring" active={activeTab === 'recurring'}>
                           <RecurringReports savedReports={allSavedReports} recurringReports={recurringReports}   onReportRecurringCreated={(savedReportId) => {
                                setPaymentSavedReports((prev) =>
                                prev.map((r) =>
                                    r.savedReportId === savedReportId ? { ...r, isRecurring: true } : r
                                )
                                );
                                setSubscriptionSavedReports((prev) =>
                                prev.map((r) =>
                                    r.savedReportId === savedReportId ? { ...r, isRecurring: true } : r
                                )
                                );
                                setAgingSavedReports((prev) =>
                                prev.map((r) =>
                                    r.savedReportId === savedReportId ? { ...r, isRecurring: true } : r
                                )
                                );
                                setRedeemedValidationsSavedReports((prev) =>
                                prev.map((r) =>
                                    r.savedReportId === savedReportId ? { ...r, isRecurring: true } : r
                                )
                                );
                                setCustomSavedReports((prev) =>
                                prev.map((r) =>
                                    r.savedReportId === savedReportId ? { ...r, isRecurring: true } : r
                                )
                                );
                            }}/>
                        </Tab.Pane>
                    </TabContent>
                </Col>
            </Row>
        </Container>
    );
} 